import { useState } from "react";
import { ClipboardList } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { Select } from "@/components/ui/Select";
import { useModalFormSession } from "@/hooks/useModalFormSession";
import {
  APONTADOR_TIPO_LABELS,
  type ApontadorDto,
  type ApontadorFormValues,
  type ApontadorTipo,
  type ContratoCarradasDto,
} from "@/services/carradas";
import { extractApiError } from "@/services/locacoes";
import { upperLocacaoText } from "./upperLocacaoText";

function emptyForm(): ApontadorFormValues {
  return {
    nome: "",
    telefone: "",
    cpf: "",
    funcao: "",
    tipoApontador: "obra",
    status: "ativo",
    mobileEnabled: false,
    contratoIdsPermitidos: [],
  };
}

function fromDto(a: ApontadorDto): ApontadorFormValues {
  return {
    nome: upperLocacaoText(a.nome),
    telefone: a.telefone,
    cpf: a.cpf,
    funcao: upperLocacaoText(a.funcao),
    tipoApontador: a.tipoApontador === "jazida" ? "jazida" : "obra",
    status: a.status,
    mobileEnabled: a.mobileEnabled,
    contratoIdsPermitidos: a.contratoIdsPermitidos ?? [],
  };
}

interface Props {
  open: boolean;
  editing: ApontadorDto | null;
  contratos: ContratoCarradasDto[];
  busy?: boolean;
  onClose: () => void;
  onSave: (values: ApontadorFormValues, editingId: string | null) => Promise<void>;
  onDelete?: () => void;
}

export function ModalApontador({ open, editing, contratos, busy, onClose, onSave, onDelete }: Props) {
  const [values, setValues] = useState<ApontadorFormValues>(emptyForm());
  const [error, setError] = useState<string | null>(null);

  useModalFormSession(open, editing?.id ?? "new", () => {
    setValues(editing ? fromDto(editing) : emptyForm());
    setError(null);
  });

  const handleSubmit = async () => {
    if (!values.nome.trim()) {
      setError("Informe o nome do apontador.");
      return;
    }
    if (values.cpf.replace(/\D/g, "").length !== 11) {
      setError("Informe um CPF válido (11 dígitos).");
      return;
    }
    if (!values.tipoApontador) {
      setError("Informe o tipo do apontador (Jazida ou Obra).");
      return;
    }
    if (values.mobileEnabled && values.contratoIdsPermitidos.length === 0) {
      setError("Selecione ao menos um contrato para liberar o APK.");
      return;
    }
    setError(null);
    try {
      await onSave(
        {
          nome: values.nome.trim(),
          telefone: values.telefone,
          cpf: values.cpf,
          funcao: values.funcao,
          tipoApontador: values.tipoApontador,
          status: values.status,
          mobileEnabled: values.mobileEnabled,
          contratoIdsPermitidos: values.contratoIdsPermitidos,
        },
        editing?.id ?? null,
      );
      onClose();
    } catch (e) {
      setError(extractApiError(e, "Erro ao salvar."));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        <div className="flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-brand-600" />
          <span>{editing ? "Editar apontador" : "Novo apontador"}</span>
        </div>
      }
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            {editing && onDelete && (
              <Button variant="ghost" size="sm" onClick={onDelete} disabled={busy} className="text-red-600 hover:text-red-700">
                Excluir
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Cancelar
            </Button>
            <Button onClick={() => void handleSubmit()} loading={busy}>
              Salvar
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        {error && <p className="text-sm text-red-600">{error}</p>}
        <Input label="Nome *" uppercase value={values.nome} onChange={(e) => setValues((v) => ({ ...v, nome: e.target.value }))} />
        <Input label="CPF *" value={values.cpf} onChange={(e) => setValues((v) => ({ ...v, cpf: e.target.value }))} />
        <Input
          label="Telefone"
          value={values.telefone}
          onChange={(e) => setValues((v) => ({ ...v, telefone: e.target.value }))}
        />
        <Select
          label="Tipo de apontador *"
          options={[
            { value: "obra", label: APONTADOR_TIPO_LABELS.obra },
            { value: "jazida", label: APONTADOR_TIPO_LABELS.jazida },
          ]}
          value={values.tipoApontador}
          onChange={(e) =>
            setValues((v) => ({
              ...v,
              tipoApontador: (e.target.value === "jazida" ? "jazida" : "obra") as ApontadorTipo,
            }))
          }
        />
        <Input
          label="Função / observação"
          uppercase
          value={values.funcao}
          onChange={(e) => setValues((v) => ({ ...v, funcao: e.target.value }))}
          placeholder="Ex.: Barreiro, turno manhã…"
        />
        <Select
          label="Status"
          options={[
            { value: "ativo", label: "Ativo" },
            { value: "inativo", label: "Inativo" },
          ]}
          value={values.status}
          onChange={(e) =>
            setValues((v) => ({ ...v, status: e.target.value === "inativo" ? "inativo" : "ativo" }))
          }
        />
        <Select
          label="Liberar acesso ao APK SEMOG Carradas"
          options={[
            { value: "nao", label: "Não" },
            { value: "sim", label: "Sim — PIN inicial 123456" },
          ]}
          value={values.mobileEnabled ? "sim" : "nao"}
          onChange={(e) => setValues((v) => ({ ...v, mobileEnabled: e.target.value === "sim" }))}
        />
        {values.mobileEnabled && (
          <MultiSelect
            label="Contratos liberados no APK *"
            value={values.contratoIdsPermitidos}
            onChange={(contratoIdsPermitidos) => setValues((v) => ({ ...v, contratoIdsPermitidos }))}
            options={contratos.map((contrato) => ({
              value: contrato.id,
              label: `${contrato.codigoContrato} · ${contrato.clienteSiengeNome}`,
            }))}
            placeholder="Selecione os contratos"
            dropdownMinWidth={420}
          />
        )}
      </div>
    </Modal>
  );
}
